/**
 * What is on its way to this counter (owner, 26 Sep 2026: "once the driver
 * press the start delivery button at the same time respected
 * mitr/outlet/dealer will get a notification and a section which will tell
 * the order is on the way and when the driver handover the vehicle the
 * mitr/outlet/dealer will mark vehicle handover done").
 *
 * Two faces of one list. On the counter's home, the section with a "Handover
 * done" button per vehicle. Everywhere else, a one-line banner that says a
 * vehicle is on the way and takes them home to mark it — the notification,
 * since the app has no phone push. Both poll once a minute and render nothing
 * when nothing is coming.
 */
import { Link, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Truck } from 'lucide-react';
import type { DeliveryRow } from '@bright/shared';

import { api, errorMessage } from '@/lib/api';
import { formatNumber } from '@/lib/format';
import { Button, Mono, Plane, useToast } from '@/components/ui';

export const INCOMING_KEY = ['deliveries', 'incoming'] as const;

function useIncoming(enabled = true) {
  return useQuery({
    queryKey: INCOMING_KEY,
    queryFn: () => api.get<{ data: DeliveryRow[] }>('/deliveries/incoming'),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    enabled,
  });
}

function when(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', timeZone: 'Asia/Kolkata' });
}

function what(row: DeliveryRow): string {
  return [row.vehicle.model, row.vehicle.variant, row.vehicle.color].filter(Boolean).join(' · ') || 'Vehicle';
}

/** The counter's home section: each vehicle on its way, with Handover done. */
export function OnTheWaySection({ className }: { className?: string }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const incoming = useIncoming();
  const handover = useMutation({
    mutationFn: (id: string) => api.post<DeliveryRow>(`/deliveries/${id}/handover`),
    onSuccess: (row) => {
      queryClient.setQueryData<{ data: DeliveryRow[] }>(INCOMING_KEY, (old) =>
        old ? { data: old.data.filter((entry) => entry.id !== row.id) } : old,
      );
      void queryClient.invalidateQueries({ queryKey: ['deliveries'] });
      toast.success('Handover done', `${row.chassisNo} is marked delivered.`);
    },
    onError: (cause) => {
      toast.error('Could not mark the handover', errorMessage(cause));
      void queryClient.invalidateQueries({ queryKey: INCOMING_KEY });
    },
  });

  const rows = incoming.data?.data ?? [];
  if (rows.length === 0) return null;
  return (
    <Plane
      label={`On the way to you · ${formatNumber(rows.length)}`}
      note="The driver has left with these. When a vehicle is in your hands, mark the handover."
      className={className}
      raised
    >
      <ul role="list" className="flex flex-col divide-y divide-line">
        {rows.map((row) => (
          <li key={row.id} className="flex flex-col gap-3 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <span aria-hidden className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-accent-soft text-accent">
                <Truck className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <p className="truncate text-body font-medium text-ink">{what(row)}</p>
                <p className="text-caption text-muted">
                  <Mono value={row.chassisNo} size="sm" /> · Driver {row.driverName}
                  {row.startedAt ? ` · left at ${when(row.startedAt)}` : ''}
                </p>
              </div>
            </div>
            <Button
              variant="primary"
              size="sm"
              className="w-full sm:w-auto"
              loading={handover.isPending && handover.variables === row.id}
              disabled={handover.isPending}
              onClick={() => handover.mutate(row.id)}
            >
              Handover done
            </Button>
          </li>
        ))}
      </ul>
    </Plane>
  );
}

/** The one-line notice on every other page of a counter's app. */
export function OnTheWayBanner() {
  const { pathname } = useLocation();
  const onHome = pathname === '/';
  const incoming = useIncoming();
  const count = incoming.data?.data.length ?? 0;
  if (onHome || count === 0) return null;
  return (
    <Link
      to="/"
      className="flex min-h-tap items-center justify-between gap-3 rounded-control border border-accent/40 bg-accent-soft px-3 py-2 text-label text-accent transition-colors hover:bg-accent-soft/70 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
    >
      <span className="inline-flex items-center gap-2">
        <Truck className="h-4 w-4" aria-hidden />
        {count === 1 ? '1 vehicle is on the way to you' : `${formatNumber(count)} vehicles are on the way to you`}
      </span>
      <span className="inline-flex items-center gap-1 text-caption font-medium">
        Mark the handover <ArrowRight className="h-3.5 w-3.5" aria-hidden />
      </span>
    </Link>
  );
}
